/**
 * One open Web Serial connection to a Nipper: framing, request/response with
 * timeouts, Factory login and the reads/writes the EcoSalt Upgrade page needs.
 * No React here so it can be tested with a fake SerialPort.
 */
import {
  CMD,
  CellRuntimeCounter,
  FrameParser,
  NIPPER_SERIAL_OPTIONS,
  NipperFrame,
  NipperVariant,
  REPLY,
  buildPacket,
  decodeCellRuntime,
  decodeFirmware,
  decodeSerial,
  decodeVariant,
  VariantChange,
  encodeVariant,
  loginRequestData,
  solveChallenge,
  toHex,
} from "./nipperProtocol";
import { NipperLoginLevel } from "./nipperKeys";

export interface NipperRuntime {
  /** COUNTER = 17-byte reply (with 2nd-cell counter), TOTAL = older 5-byte replies. */
  format: "COUNTER" | "TOTAL" | null;
  cellRuntime: number | null;
  lowSaltRuntime: number | null;
  cell2Count: number | null;
  cell2Runtime: number | null;
  cell2LowSaltRuntime: number | null;
}

export interface NipperReading {
  serial: string | null;
  firmwareVersion: string | null;
  variant: NipperVariant;
  runtime: NipperRuntime;
  /** Undecoded reply payloads as hex, for audit / format validation. */
  raw: Record<string, string>;
}

export class NipperCommandError extends Error {}

const EMPTY_RUNTIME: NipperRuntime = {
  format: null,
  cellRuntime: null,
  lowSaltRuntime: null,
  cell2Count: null,
  cell2Runtime: null,
  cell2LowSaltRuntime: null,
};

interface Waiter {
  match: (f: NipperFrame) => boolean;
  resolve: (f: NipperFrame) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export interface NipperSessionOptions {
  onLog?: (message: string) => void;
  /** Called when the read loop ends (device unplugged or port closed). */
  onClosed?: (error?: Error) => void;
  timeoutMs?: number;
}

export class NipperSession {
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private parser = new FrameParser();
  private waiters: Waiter[] = [];
  private sequence = 0;
  private closed = false;
  private readonly timeoutMs: number;

  constructor(
    readonly port: SerialPort,
    private readonly options: NipperSessionOptions = {},
  ) {
    this.timeoutMs = options.timeoutMs ?? 1500;
  }

  private log(message: string) {
    this.options.onLog?.(message);
  }

  async open() {
    await this.port.open({ ...NIPPER_SERIAL_OPTIONS });
    if (!this.port.writable || !this.port.readable) throw new Error("Serial port is not readable/writable");
    this.writer = this.port.writable.getWriter();
    this.reader = this.port.readable.getReader();
    void this.readLoop();
  }

  get isOpen() {
    return !this.closed && !!this.writer;
  }

  private async readLoop() {
    let error: Error | undefined;
    try {
      while (!this.closed && this.reader) {
        const { value, done } = await this.reader.read();
        if (done) break;
        if (value) this.handleBytes(value);
      }
    } catch (e) {
      error = e instanceof Error ? e : new Error(String(e));
    }
    if (!this.closed) {
      this.log(error ? `Read error: ${error.message}` : "Serial port closed");
      await this.close();
      this.options.onClosed?.(error);
    }
  }

  private handleBytes(bytes: Uint8Array) {
    for (const frame of this.parser.push(bytes)) {
      this.log(`← ${frame.command} [${toHex(frame.data)}]${frame.crcOk ? "" : " (CRC mismatch)"}`);
      const index = this.waiters.findIndex((w) => w.match(frame));
      if (index >= 0) {
        const [waiter] = this.waiters.splice(index, 1);
        clearTimeout(waiter.timer);
        waiter.resolve(frame);
      }
    }
  }

  private waitFor(match: (f: NipperFrame) => boolean, label: string, timeoutMs = this.timeoutMs) {
    return new Promise<NipperFrame>((resolve, reject) => {
      const waiter: Waiter = {
        match,
        resolve,
        reject,
        timer: setTimeout(() => {
          this.waiters = this.waiters.filter((w) => w !== waiter);
          reject(new NipperCommandError(`No reply to ${label}`));
        }, timeoutMs),
      };
      this.waiters.push(waiter);
    });
  }

  async send(command: string, data: number[] = []) {
    if (!this.writer || this.closed) throw new NipperCommandError("Nipper is not connected");
    const packet = buildPacket(command, data, this.sequence);
    this.sequence = (this.sequence + 1) & 0xff;
    this.log(`→ ${command} [${toHex(data)}]`);
    await this.writer.write(packet);
  }

  /** Sends `command` and resolves with its lowercase reply (or a reject/privilege error). */
  async request(command: string, data: number[] = [], timeoutMs?: number) {
    const reply = command.toLowerCase();
    const upper = command.toUpperCase().charCodeAt(0);
    const pending = this.waitFor(
      (f) =>
        f.command === reply ||
        ((f.command === REPLY.REJECT || f.command === REPLY.INSUFFICIENT_PRIVILEGE || f.command === REPLY.ERROR) &&
          (f.data.length === 0 || f.data[0] === upper)),
      command,
      timeoutMs,
    );
    await this.send(command, data);
    const frame = await pending;
    if (frame.command !== reply) {
      const why = frame.command === REPLY.INSUFFICIENT_PRIVILEGE ? "insufficient privilege" : "rejected";
      throw new NipperCommandError(`Nipper ${why} command ${command}`);
    }
    return frame;
  }

  /** L(level) → x(challenge) → X(response) → a. Resolves false on any failure. */
  async login(level: NipperLoginLevel = "factory"): Promise<boolean> {
    try {
      const challenge = this.waitFor((f) => f.command === CMD.CHALLENGE.toLowerCase(), "login");
      await this.send(CMD.LOGIN_REQUEST, loginRequestData(level));
      const frame = await challenge;
      const response = solveChallenge(level, frame.data);
      if (!response) {
        this.log(`Invalid challenge length ${frame.data.length}`);
        return false;
      }
      const outcome = this.waitFor(
        (f) => [REPLY.ACCEPT, REPLY.REJECT, REPLY.INSUFFICIENT_PRIVILEGE, REPLY.ERROR].includes(f.command as any),
        "challenge response",
      );
      await this.send(CMD.CHALLENGE, response);
      const result = await outcome;
      return result.command === REPLY.ACCEPT;
    } catch (e) {
      this.log(`Login attempt failed: ${e instanceof Error ? e.message : String(e)}`);
      return false;
    }
  }

  async readVariant(raw?: Record<string, string>): Promise<NipperVariant> {
    const frame = await this.request(CMD.VARIANT);
    if (raw) raw.W = toHex(frame.data);
    const variant = decodeVariant(frame.data);
    if (!variant) throw new NipperCommandError(`Unexpected variant reply [${toHex(frame.data)}]`);
    return variant;
  }

  /**
   * Q 0xFF. Newer firmware answers once with all counters (17 bytes). Older
   * firmware answers with one [type, value] at a time, so each known type is
   * then asked for individually; types that do not answer stay null.
   */
  async readRuntime(raw?: Record<string, string>): Promise<NipperRuntime> {
    const first = await this.request(CMD.CELL_RUNTIME, [0xff]);
    if (raw) raw.Q = toHex(first.data);
    const decoded = decodeCellRuntime(first.data);
    if (decoded?.format === "COUNTER") {
      const { format, ...rest } = decoded as CellRuntimeCounter;
      return { format, ...rest };
    }
    const runtime: NipperRuntime = { ...EMPTY_RUNTIME, format: "TOTAL" };
    if (decoded?.format === "TOTAL" && decoded.field) runtime[decoded.field] = decoded.value;
    for (const type of [1, 2, 3, 4]) {
      try {
        const frame = await this.request(CMD.CELL_RUNTIME, [type]);
        if (raw) raw[`Q${type}`] = toHex(frame.data);
        const part = decodeCellRuntime(frame.data);
        if (part?.format === "TOTAL" && part.field) runtime[part.field] = part.value;
      } catch {
        // Older firmware may not answer every type; keep what we have.
      }
    }
    return runtime;
  }

  /** Serial, firmware, variant and runtime. Serial/firmware failures are tolerated (null). */
  async readAll(): Promise<NipperReading> {
    const raw: Record<string, string> = {};
    const optional = async (command: string, decode: (d: number[]) => string | null) => {
      try {
        const frame = await this.request(command);
        raw[command] = toHex(frame.data);
        return decode(frame.data);
      } catch (e) {
        this.log(`${command}: ${e instanceof Error ? e.message : String(e)}`);
        return null;
      }
    };
    const serial = await optional(CMD.SERIAL_NUMBER, decodeSerial);
    const firmwareVersion = await optional(CMD.FIRMWARE, decodeFirmware);
    const variant = await this.readVariant(raw);
    let runtime = EMPTY_RUNTIME;
    try {
      runtime = await this.readRuntime(raw);
    } catch (e) {
      this.log(`Runtime: ${e instanceof Error ? e.message : String(e)}`);
    }
    return { serial, firmwareVersion, variant, runtime, raw };
  }

  /**
   * Asks the Nipper to reboot (`B` with data `1`), matching nipper-webserial-comms.
   * The serial link usually drops after this; the caller should reconnect.
   */
  async reboot() {
    await this.send(CMD.SWITCH_TO_BOOTLOADER, [1]);
  }

  /**
   * Writes the variant with only the given fields changed, then re-reads it.
   * Throws unless the Nipper reports those new values.
   */
  async writeVariant(current: NipperVariant, change: VariantChange): Promise<NipperVariant> {
    const outcome = this.waitFor(
      (f) => [REPLY.ACCEPT, REPLY.REJECT, REPLY.INSUFFICIENT_PRIVILEGE, REPLY.ERROR, "w"].includes(f.command),
      "variant write",
    ).catch(() => null); // Some firmware does not acknowledge writes; the re-read below is the check.
    await this.send(CMD.VARIANT, encodeVariant(current, change));
    const ack = await outcome;
    if (ack && ack.command !== REPLY.ACCEPT && ack.command !== "w") {
      throw new NipperCommandError(
        ack.command === REPLY.INSUFFICIENT_PRIVILEGE ? "Nipper refused the write (insufficient privilege)" : "Nipper rejected the write",
      );
    }
    const after = await this.readVariant();
    if (change.cellSize != null && after.cellSize !== change.cellSize) {
      throw new NipperCommandError(`Nipper still reports ${after.cellSize}g after writing ${change.cellSize}g`);
    }
    if (change.regionCode != null && after.regionCode !== change.regionCode) {
      throw new NipperCommandError("Nipper did not accept the region");
    }
    if (change.brandCode != null && after.brandCode !== change.brandCode) {
      throw new NipperCommandError("Nipper did not accept the brand");
    }
    if (change.lowSalt != null && after.lowSalt !== change.lowSalt) {
      throw new NipperCommandError("Nipper did not accept the low salt setting");
    }
    return after;
  }

  async writeCellSize(current: NipperVariant, cellSize: number): Promise<NipperVariant> {
    return this.writeVariant(current, { cellSize });
  }

  async close() {
    if (this.closed) return;
    this.closed = true;
    for (const w of this.waiters) {
      clearTimeout(w.timer);
      w.reject(new NipperCommandError("Nipper disconnected"));
    }
    this.waiters = [];
    try {
      await this.reader?.cancel();
    } catch {
      /* ignore */
    }
    try {
      this.reader?.releaseLock();
    } catch {
      /* ignore */
    }
    try {
      await this.writer?.close();
    } catch {
      /* ignore */
    }
    try {
      this.writer?.releaseLock();
    } catch {
      /* ignore */
    }
    this.reader = null;
    this.writer = null;
    try {
      await this.port.close();
    } catch {
      /* ignore */
    }
  }
}
